import {
  MONAIExperiment,
  MONAI15InstanceDetails,
  MONAIInstanceDetails,
} from './types';
import { MONAI_BUNDLES } from './bundles';
import { MONAI_DATASETS } from './datasets';

// Legacy MONAI mock function for backward compatibility
export const mockMONAIInstanceDetails = (taskId: string): MONAIInstanceDetails => ({
  monaiVersion: '1.5.1',
  status: 'active',
  pipelines: [
    {
      name: 'lung_nodule_segmentation',
      type: 'segmentation',
      modality: 'CT',
      status: 'training',
      metrics: {
        diceScore: 0.892,
        epoch: 45,
        totalEpochs: 100,
      },
    },
    {
      name: 'chest_xray_classification',
      type: 'classification',
      modality: 'X-Ray',
      status: 'inference',
      metrics: {
        accuracy: 0.934,
        auc: 0.967,
      },
    },
  ],
  bundlesInstalled: [
    'spleen_ct_segmentation',
    'pancreas_ct_dints_segmentation',
    'lung_nodule_ct_detection',
  ],
  dataPath: '/workspace/medical_data',
  notebookUrl: `http://localhost:8888/lab?token=monai-${taskId.slice(0, 8)}`,
});

// Mock function to generate MONAI 1.5.1 experiments
export const mockMONAI15InstanceDetails = (taskId: string): MONAI15InstanceDetails => {
  const spleenBundle = MONAI_BUNDLES.find(b => b.id === 'spleen_ct_segmentation')!;
  const chestBundle = MONAI_BUNDLES.find(b => b.id === 'chest_xray_class')!;
  const liverBundle = MONAI_BUNDLES.find(b => b.id === 'liver_tumor_seg')!;

  const spleenDataset = MONAI_DATASETS.find(d => d.id === 'task09_spleen')!;
  const chestDataset = MONAI_DATASETS.find(d => d.id === 'chestmnist')!;
  const liverDataset = MONAI_DATASETS.find(d => d.id === 'task03_liver')!;

  return {
    monaiVersion: '1.5.1',
    status: 'active',
    gpuUtilization: 78,
    gpuMemoryUsed: 62,
    gpuMemoryTotal: 80,
    experiments: [
      {
        id: 'exp-monai-001',
        name: 'spleen_segmentation_v1',
        task: 'segmentation',
        modality: 'CT',
        status: 'training',
        bundle: spleenBundle,
        dataset: spleenDataset,
        config: {
          epochs: 100,
          batchSize: 4,
          learningRate: 1e-4,
          optimizer: 'adamw',
          scheduler: 'cosine',
          augmentations: ['RandFlipd', 'RandRotate90d', 'RandScaleIntensityd'],
          mixedPrecision: true,
          cachingStrategy: 'persistent',
          numWorkers: 4,
        },
        metrics: {
          currentEpoch: 67,
          totalEpochs: 100,
          trainLoss: 0.0423,
          valLoss: 0.0512,
          diceScore: 0.952,
        },
        progress: 67,
        training: {
          startedAt: Date.now() - 7200000,
          estimatedCompletion: Date.now() + 3600000,
          gpuUtilization: 85,
          gpuMemoryUsed: 24,
        },
        exports: [],
        createdAt: Date.now() - 7200000,
        updatedAt: Date.now(),
      },
      {
        id: 'exp-monai-002',
        name: 'chest_xray_classifier_v2',
        task: 'classification',
        modality: 'X-Ray',
        status: 'completed',
        bundle: chestBundle,
        dataset: chestDataset,
        config: {
          epochs: 50,
          batchSize: 32,
          learningRate: 1e-3,
          optimizer: 'adam',
          scheduler: 'step',
          augmentations: ['RandRotate', 'RandZoom', 'RandGaussianNoise'],
          mixedPrecision: true,
          cachingStrategy: 'cache',
          numWorkers: 8,
        },
        metrics: {
          currentEpoch: 50,
          totalEpochs: 50,
          trainLoss: 0.1234,
          valLoss: 0.1456,
          accuracy: 0.923,
          auc: 0.967,
        },
        progress: 100,
        training: {
          startedAt: Date.now() - 86400000,
          gpuUtilization: 0,
          gpuMemoryUsed: 0,
        },
        exports: [
          {
            id: 'export-001',
            format: 'onnx',
            status: 'completed',
            path: '/workspace/exports/chest_xray_v2.onnx',
            size: '125 MB',
            createdAt: Date.now() - 82800000,
          },
        ],
        createdAt: Date.now() - 172800000,
        updatedAt: Date.now() - 82800000,
      },
      {
        id: 'exp-monai-003',
        name: 'liver_tumor_seg_v1',
        task: 'segmentation',
        modality: 'CT',
        status: 'queued',
        bundle: liverBundle,
        dataset: liverDataset,
        config: {
          epochs: 150,
          batchSize: 2,
          learningRate: 1e-4,
          optimizer: 'novograd',
          scheduler: 'warmup_cosine',
          augmentations: ['RandFlipd', 'RandRotate90d', 'RandCropByPosNegLabeld'],
          mixedPrecision: true,
          cachingStrategy: 'persistent',
          numWorkers: 4,
        },
        metrics: {
          currentEpoch: 0,
          totalEpochs: 150,
          trainLoss: 0,
        },
        progress: 0,
        training: {
          startedAt: 0,
          gpuUtilization: 0,
          gpuMemoryUsed: 0,
        },
        exports: [],
        createdAt: Date.now() - 3600000,
        updatedAt: Date.now() - 3600000,
      },
    ],
    datasets: MONAI_DATASETS,
    bundles: MONAI_BUNDLES,
    workspacePaths: {
      root: '/workspace',
      datasets: '/workspace/data',
      experiments: '/workspace/experiments',
      exports: '/workspace/exports',
      logs: '/workspace/logs',
    },
    notebookUrl: `http://localhost:8888/lab?token=monai-${taskId.slice(0, 8)}`,
  };
};
